import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ShieldCheck } from '@phosphor-icons/react';
import AxialupLogo from '../components/AxialupLogo';

const formatApiError = (detail) => {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
};

const IS_TEST_ENV = /preview\.emergentagent\.com$|^localhost$|^127\.0\.0\.1$/.test(window.location.hostname);

const EnvBadge = () => (
  <span
    data-testid="login-env-badge"
    title={IS_TEST_ENV ? 'Test site — test database ke login chalenge' : 'Live site — live database ke login chalenge'}
    className={`absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-widest ${IS_TEST_ENV ? 'bg-amber-400 text-amber-950' : 'bg-red-600 text-white'}`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${IS_TEST_ENV ? 'bg-amber-950' : 'bg-white animate-pulse'}`} />
    {IS_TEST_ENV ? 'TEST' : 'LIVE'}
  </span>
);

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [twofa, setTwofa] = useState(null);  // {challenge_token, name, role} when 2FA required
  const [code, setCode] = useState('');
  const { login, verify2FA } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const justRegistered = searchParams.get('registered') === 'true';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data?.requires_2fa) {
        setTwofa(data);
        setLoading(false);
        return;
      }
      if (data?.requires_2fa_setup) {
        // Soft-block: role-based policy requires 2FA — force user to setup
        navigate('/security/2fa?required=1');
        return;
      }
      navigate('/');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429) {
        setError(detail || 'Bahut zyada attempts. Thodi der baad try karo.');
      } else if (status === 403) {
        setError(detail || 'Account approved nahi hai. Admin se contact karo.');
      } else if (status === 401) {
        setError(detail || 'Email ya password galat hai.');
      } else if (!err.response) {
        setError('Server se connect nahi ho paya. Internet check karo.');
      } else {
        setError(formatApiError(detail) || 'Login failed. Please try again.');
      }
      setLoading(false);
    }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    setError('');
    if (code.length < 6) { setError('6-digit code daalo'); return; }
    setLoading(true);
    try {
      await verify2FA(twofa.challenge_token, code);
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(formatApiError(detail) || 'Code verify fail. Dobara try karein.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-gray-200 shadow-sm overflow-hidden rounded" data-testid="login-page">
        {/* Header Banner */}
        <div className="relative bg-gradient-to-br from-[#0C1D3F] via-[#002FA7] to-[#0EA5E9] px-6 py-7 text-center">
          <EnvBadge />
          <div className="flex justify-center mb-3">
            <AxialupLogo size={64} srOnly />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight leading-tight">
            AXIAL<span className="text-cyan-300">UP</span>
          </h1>
          <p className="text-[10px] sm:text-[11px] text-cyan-100/90 mt-1 tracking-[0.2em] uppercase font-semibold">OPC Pvt Ltd · Digital Services</p>
          <p className="text-[10px] sm:text-[11px] text-blue-200/70 mt-2 tracking-wide">Secure Booking · Ledger · Reconciliation Platform</p>
        </div>

        <div className="px-6 py-6">
          <h2 className="text-base font-semibold text-gray-900 mb-5">Sign in to your account</h2>

          {justRegistered && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-700 text-sm" data-testid="registration-pending-msg">
              Registration successful! Your account is waiting for admin approval.
            </div>
          )}
          
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          
          <form onSubmit={twofa ? handle2FA : handleSubmit} className="space-y-4">
            {!twofa && (
              <>
            <div>
              <Label htmlFor="email" className="form-label text-sm text-gray-700">Email / Mobile</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input h-11"
                placeholder="Enter your email or mobile"
                required
                data-testid="login-email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="form-label text-sm text-gray-700">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input h-11"
                placeholder="Enter your password"
                required
                data-testid="login-password-input"
              />
            </div>
              </>
            )}

            {twofa && (
              <div className="space-y-3" data-testid="login-2fa-step">
                <div className="bg-violet-50 border border-violet-200 rounded p-3 text-xs text-violet-900">
                  🛡️ <b>Two-Factor Authentication required</b><br />
                  Welcome <b>{twofa.name}</b>. Apne Authenticator app me se 6-digit code daalein.
                </div>
                <div>
                  <Label className="form-label text-sm text-gray-700">Authenticator code</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                    className="form-input h-12 text-center text-2xl tracking-[0.5em] font-mono"
                    placeholder="••••••"
                    maxLength={6}
                    required
                    data-testid="login-2fa-code-input"
                  />
                </div>
                <button type="button" onClick={() => { setTwofa(null); setCode(''); setError(''); }} className="text-xs text-gray-500 hover:text-violet-700 hover:underline">
                  ← Use different account
                </button>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-[#002FA7] hover:bg-[#001f6e] text-white h-11 font-semibold flex items-center justify-center gap-2"
              disabled={loading}
              data-testid={twofa ? 'login-2fa-submit-btn' : 'login-submit-btn'}
            >
              <ShieldCheck size={18} />
              {loading ? (twofa ? 'Verifying…' : 'Signing in...') : (twofa ? 'Verify code' : 'Secure Login')}
            </Button>
          </form>

          <div className="text-center mt-4">
            <Link to="/forgot-password" className="text-xs text-[#002FA7] hover:underline" data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </div>
          
          <p className="mt-4 text-center text-sm text-gray-600">
            New user?{' '}
            <Link to="/register" className="text-[#002FA7] font-semibold hover:underline" data-testid="register-link">
              Create account
            </Link>
          </p>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4">
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 text-[11px] text-gray-400">
            <Link to="/terms" className="hover:text-[#002FA7] hover:underline">Terms</Link>
            <span>|</span>
            <Link to="/privacy" className="hover:text-[#002FA7] hover:underline">Privacy</Link>
            <span>|</span>
            <Link to="/refund-policy" className="hover:text-[#002FA7] hover:underline">Refund</Link>
            <span>|</span>
            <Link to="/grievance-policy" className="hover:text-[#002FA7] hover:underline">Grievance</Link>
            <span>|</span>
            <Link to="/support-help" className="hover:text-[#002FA7] hover:underline">Support</Link>
            <span>|</span>
            <Link to="/data-deletion" className="hover:text-[#002FA7] hover:underline">Data Deletion</Link>
            <span>|</span>
            <Link to="/whatsapp-compliance" className="hover:text-[#002FA7] hover:underline">WhatsApp Policy</Link>
            <span>|</span>
            <Link to="/about-business" className="hover:text-[#002FA7] hover:underline">About</Link>
          </div>
          <p className="text-center text-[10px] text-gray-400 mt-2">
            support@axialup.in | 9128843229
          </p>
          <p className="text-center text-[10px] text-gray-500 mt-2 font-medium">
            AXIALUP OPC PVT LTD · GSTIN: 10AAYCA0050H1Z1 · Registered under Companies Act 2013
          </p>
        </div>
      </div>
    </div>
  );
}
